import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { LayoutHeader } from "@/Header";
import { Loading } from "@/Loading";
import MessageModal from "@/MessageModal";
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useCompanyTicketList } from "~/services/customer/home";
import { useTicketPreOrder } from "~/services/payment/ticket";
import { useToastStore } from "&/toast";
import { usePet } from "^/common/usePet";
import type { PetDataType, TicketPaymentPreOrderRequest } from "~/types/common";
import { getErrorMessage } from "%/errorCode";
import { useNavVisibilityStore } from "&/navVisibility";

interface SubscriptionListParams {
  companySeq?: string;
  selectedPlans?: string | Array<{ planSeq: number; quantity: number }>; // 인코딩된 선택된 이용권 정보 또는 배열
  restoreState?: string; // 복원할 선택 상태 (JSON 문자열)
}

export const Route = createFileRoute("/customer/layout/subscription-list")({
  component: SubscriptionList,
  validateSearch: (search: Record<string, unknown>): SubscriptionListParams => {
    return {
      // URL 직렬화 방식에 따라 숫자/문자열 어느 쪽으로 와도 문자열로 정규화
      companySeq:
        search.companySeq != null ? String(search.companySeq) : undefined,
      selectedPlans: search.selectedPlans as string,
      restoreState: search.restoreState as string,
    };
  },
});

interface SubscriptionPlan {
  planSeq: number;
  planName: string;
  price: number;
  discountRate: number;
  discountedPrice: number;
  badges: string[];
  content?: string;
  minPurchaseQty?: number | null; // 데이케어: 최소 구매 수량
  minUseQty?: number | null; // 데이케어: 최소 이용 수량
}

function SubscriptionList() {
  const {
    companySeq,
    selectedPlans: selectedPlansParam,
    restoreState,
  } = Route.useSearch();
  const companySeqNumber = companySeq ? Number.parseInt(companySeq, 10) : 0;
  const navigate = useNavigate();
  const { add } = useToastStore();
  const { mutate: createTicketPreOrder, isPending: isCreatingPreOrder } =
    useTicketPreOrder();

  // 선택된 이용권과 수량 관리 (planSeq -> quantity)
  const [selectedPlans, setSelectedPlans] = useState<Map<number, number>>(
    new Map()
  );
  const hasRestoredRef = useRef(false); // 복원 여부 추적

  // 이용권을 하나라도 고르면 하단 nav 자리에 구매 CTA를 상시 노출한다.
  // (스크롤 위치로 교대 노출하면 최상단에서 선택했을 때 CTA가 영영 안 보인다)
  const hasSelection = selectedPlans.size > 0;
  const setNavVisible = useNavVisibilityStore((s) => s.setVisible);
  useEffect(() => {
    setNavVisible(!hasSelection);
    return () => setNavVisible(true);
  }, [hasSelection, setNavVisible]);

  // 펫 선택 모달 상태
  const [isPetModalOpen, setIsPetModalOpen] = useState(false);
  const [selectedPetSeq, setSelectedPetSeq] = useState<number | null>(null);

  // 보유한 펫 목록 조회
  const { usePetList } = usePet();
  const { data: petList } = usePetList();

  // API에서 실제 이용권 목록 가져오기
  const {
    data: ticketList,
    isLoading,
    error,
  } = useCompanyTicketList(companySeqNumber, !!companySeqNumber);

  // API 응답을 SubscriptionPlan 형식으로 변환
  const allPlans: SubscriptionPlan[] = useMemo(() => {
    if (!ticketList || !Array.isArray(ticketList)) {
      return [];
    }
    return ticketList.map((ticket) => ({
      planSeq: ticket.productTicketSeq,
      planName: ticket.name,
      content: ticket.contents,
      price: ticket.cost,
      discountRate: ticket.discount,
      discountedPrice: ticket.amount,
      badges: ticket.badge || [],
      minPurchaseQty: ticket.minPurchaseQty ?? null,
      minUseQty: ticket.minUseQty ?? null,
    }));
  }, [ticketList]);

  // selectedPlansParam에서 허용된 planSeq 목록 추출
  const allowedPlanSeqs = useMemo(() => {
    if (!selectedPlansParam) return null;

    try {
      let decoded: Array<{ planSeq: number; quantity: number }>;

      if (Array.isArray(selectedPlansParam)) {
        decoded = selectedPlansParam;
      } else if (typeof selectedPlansParam === "string") {
        decoded = JSON.parse(decodeURIComponent(selectedPlansParam));
      } else {
        return null;
      }

      if (Array.isArray(decoded)) {
        return new Set(decoded.map((item) => item.planSeq));
      }
    } catch (error) {
      console.error("선택된 이용권 파라미터 파싱 실패:", error);
    }
    return null;
  }, [selectedPlansParam]);

  // 표시할 이용권 목록 (selectedPlansParam이 있으면 해당 이용권만, 없으면 전체)
  const plans: SubscriptionPlan[] = useMemo(() => {
    if (allowedPlanSeqs === null) {
      // selectedPlansParam이 없으면 전체 이용권 표시
      return allPlans;
    }
    // selectedPlansParam이 있으면 해당 이용권만 필터링
    return allPlans.filter((plan) => allowedPlanSeqs.has(plan.planSeq));
  }, [allPlans, allowedPlanSeqs]);

  // URL 파라미터에서 선택된 이용권 정보 복원 (plans가 로드된 후)
  useEffect(() => {
    if (selectedPlansParam && plans.length > 0 && !hasRestoredRef.current) {
      try {
        console.log(
          "선택된 이용권 복원 시도, 파라미터:",
          selectedPlansParam,
          "타입:",
          typeof selectedPlansParam
        );

        // selectedPlansParam이 이미 배열인지 문자열인지 확인
        let decoded: Array<{ planSeq: number; quantity: number }>;

        if (Array.isArray(selectedPlansParam)) {
          // 이미 배열로 파싱된 경우
          decoded = selectedPlansParam;
        } else if (typeof selectedPlansParam === "string") {
          // 문자열인 경우 JSON 파싱
          decoded = JSON.parse(decodeURIComponent(selectedPlansParam));
        } else {
          console.error(
            "선택된 이용권 파라미터 형식이 올바르지 않습니다:",
            selectedPlansParam
          );
          return;
        }

        console.log("복원할 데이터:", decoded);

        if (Array.isArray(decoded)) {
          const restoredMap = new Map<number, number>();
          decoded.forEach(
            ({ planSeq, quantity }: { planSeq: number; quantity: number }) => {
              if (planSeq && quantity) {
                // plans에 해당 planSeq가 존재하는지 확인
                const planExists = plans.some((p) => p.planSeq === planSeq);
                console.log(
                  `planSeq ${planSeq} 존재 여부:`,
                  planExists,
                  "plans:",
                  plans.map((p) => p.planSeq)
                );
                if (planExists) {
                  restoredMap.set(planSeq, quantity);
                }
              }
            }
          );
          if (restoredMap.size > 0) {
            console.log("선택된 이용권 정보 복원 성공:", restoredMap);
            setSelectedPlans(restoredMap);
            hasRestoredRef.current = true;
          } else {
            console.log("복원할 이용권이 없음");
          }
        }
      } catch (error) {
        console.error("선택된 이용권 정보 복원 실패:", error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlansParam, plans.length]);

  // 커스텀 뒤로가기 핸들러
  const handleBackClick = useCallback(() => {
    // restoreState가 있으면 예약 페이지로 돌아가면서 선택 상태 복원 (명시 이동 필요)
    if (restoreState) {
      navigate({
        to: "/customer/layout/reservation-with-subscription",
        search: {
          // 문자열로 넘기면 JSON 직렬화로 URL이 %22145%22 가 되므로 숫자로 전달
          companySeq: companySeqNumber
            ? (companySeqNumber as unknown as string)
            : undefined,
          cdProductType: undefined,
          restoreState: restoreState, // 이미 인코딩된 문자열이므로 그대로 전달
        },
        replace: true, // 히스토리에 새 항목 추가하지 않고 현재 항목 교체
      });
      return;
    }
    // 일반 진입은 브라우저 back으로 복귀해야 히스토리에 중복 엔트리가 안 생긴다
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    // 딥링크(스택 없음) 폴백: 매장 상세로 이동
    if (companySeqNumber) {
      navigate({
        to: "/customer/layout/home-serach-detail",
        search: { companySeq: companySeqNumber as unknown as string },
        replace: true,
      });
    }
  }, [companySeqNumber, navigate, restoreState]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("ko-KR").format(price);
  };

  // 이용권의 최소 구매 수량 (없으면 1)
  const getMinPurchaseQty = (planSeq: number) => {
    const plan = plans.find((p) => p.planSeq === planSeq);
    return plan?.minPurchaseQty ?? 1;
  };

  // 이용권 선택/해제 (카드 클릭)
  const handlePlanToggle = (planSeq: number) => {
    // selectedPlansParam이 있고, 해당 planSeq가 허용 목록에 없으면 선택 불가
    if (allowedPlanSeqs !== null && !allowedPlanSeqs.has(planSeq)) {
      return;
    }

    const newSelectedPlans = new Map(selectedPlans);
    if (newSelectedPlans.has(planSeq)) {
      // 이미 선택된 경우 해제
      newSelectedPlans.delete(planSeq);
    } else {
      // 선택되지 않은 경우 선택 (기본 수량 = 최소 구매 수량)
      newSelectedPlans.set(planSeq, getMinPurchaseQty(planSeq));
    }
    setSelectedPlans(newSelectedPlans);
  };

  // 수량 증가
  const handleQuantityIncrease = (planSeq: number) => {
    const newSelectedPlans = new Map(selectedPlans);
    const currentQuantity = newSelectedPlans.get(planSeq) || 0;
    newSelectedPlans.set(planSeq, currentQuantity + 1);
    setSelectedPlans(newSelectedPlans);
  };

  // 수량 감소
  const handleQuantityDecrease = (planSeq: number) => {
    const newSelectedPlans = new Map(selectedPlans);
    const currentQuantity = newSelectedPlans.get(planSeq) || 0;
    const minQty = getMinPurchaseQty(planSeq);
    if (currentQuantity > minQty) {
      newSelectedPlans.set(planSeq, currentQuantity - 1);
    } else {
      // 최소 구매 수량 이하이면 선택 해제
      newSelectedPlans.delete(planSeq);
    }
    setSelectedPlans(newSelectedPlans);
  };

  // 할인 후 총 금액 계산
  const totalAmount = useMemo(() => {
    let total = 0;
    selectedPlans.forEach((quantity, planSeq) => {
      const plan = plans.find((p) => p.planSeq === planSeq);
      if (plan) {
        total += plan.discountedPrice * quantity;
      }
    });
    return total;
  }, [selectedPlans, plans]);

  // 할인 전 총 금액 계산
  const totalOriginalAmount = useMemo(() => {
    let total = 0;
    selectedPlans.forEach((quantity, planSeq) => {
      const plan = plans.find((p) => p.planSeq === planSeq);
      if (plan) {
        total += plan.price * quantity;
      }
    });
    return total;
  }, [selectedPlans, plans]);

  // 할인 금액 계산
  const totalDiscountAmount = totalOriginalAmount - totalAmount;

  // 선택된 이용권 총 개수
  const totalQuantity = useMemo(() => {
    let total = 0;
    selectedPlans.forEach((quantity) => {
      total += quantity;
    });
    return total;
  }, [selectedPlans]);

  // 구매하기 버튼 클릭 - 펫 선택 모달 열기
  const handlePurchase = () => {
    if (selectedPlans.size === 0) {
      add("구매할 이용권을 선택해주세요.");
      return;
    }

    if (!petList || petList.length === 0) {
      add("등록된 반려동물이 없습니다.");
      return;
    }

    // 최소 구매 수량 검증 (URL 복원 등으로 미달 상태가 될 수 있음)
    for (const [planSeq, quantity] of selectedPlans) {
      const minQty = getMinPurchaseQty(planSeq);
      if (quantity < minQty) {
        const plan = plans.find((p) => p.planSeq === planSeq);
        add(
          `${plan?.planName ?? "이용권"}은(는) 최소 ${minQty}개부터 구매할 수 있습니다.`
        );
        return;
      }
    }

    // 펫이 한 마리뿐이면 미리 선택해 둠
    setSelectedPetSeq(petList.length === 1 ? petList[0].petSeq : null);
    setIsPetModalOpen(true);
  };

  // 펫 선택 후 실제 Pre Order 호출
  const handleConfirmPetAndPurchase = () => {
    if (selectedPetSeq == null) {
      add("반려동물을 선택해주세요.");
      return;
    }

    // 선택된 이용권을 API 요청 형식으로 변환 (모든 항목에 동일한 petSeq 적용)
    const requestData: TicketPaymentPreOrderRequest[] = [];
    selectedPlans.forEach((quantity, planSeq) => {
      requestData.push({
        productTicketSeq: planSeq,
        qty: quantity,
        petSeq: selectedPetSeq,
      });
    });

    setIsPetModalOpen(false);

    // Pre Order API 호출
    createTicketPreOrder(requestData, {
      onSuccess: (response) => {
        console.log("이용권 Pre Order 성공:", response);

        // 선택된 이용권 정보를 배열로 변환 (복원을 위해)
        const selectedPlansArray = Array.from(selectedPlans.entries()).map(
          ([planSeq, quantity]) => ({ planSeq, quantity })
        );

        // 결제 정보를 URL 파라미터로 인코딩하여 결제 페이지로 이동
        const paymentData = {
          orderId: response.data.orderId,
          costAmount: response.data.amount,
          purchaseQuantity: totalQuantity,
          customerKey: response.data.customerKey,
          customerEmail: response.data.customerEmail,
          customerName: response.data.customerName,
          customerMobilePhone: response.data.customerMobilePhone, // 백엔드에서 추가된 전화번호
          widgetClientKey: response.data.widgetClientKey,
          paymentType: "TICKET", // 이용권 구매임을 표시
          companySeq: companySeqNumber, // companySeq 포함
          selectedPlans: selectedPlansArray, // 선택된 이용권 정보 포함
          restoreState: restoreState, // 예약 페이지 복원을 위한 상태 정보 포함
        };

        const encodedPaymentData = encodeURIComponent(
          JSON.stringify(paymentData)
        );

        navigate({
          to: "/customer/layout/payment",
          search: {
            paymentData: encodedPaymentData,
          },
        });
      },
      onError: (error) => {
        console.error("이용권 Pre Order 실패:", error);
        add(
          getErrorMessage(error, "이용권 구매 요청에 실패했습니다. 다시 시도해주세요.")
        );
      },
    });
  };

  if (isLoading) {
    return (
      <>
        <LayoutHeader
          title="이용권 목록"
          hasBackBtn
          hasFootPrint={false}
          onBackClick={handleBackClick}
        />
        <div className="flex justify-center items-center flex-auto">
          <Loading visible={true} />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <LayoutHeader
          title="이용권 목록"
          hasBackBtn
          hasFootPrint={false}
          onBackClick={handleBackClick}
        />
        <div className="flex justify-center items-center flex-auto">
          <div className="text-center text-gray-500">
            <p>이용권 정보를 불러올 수 없습니다.</p>
            <p className="mt-1 text-sm">잠시 후 다시 시도해주세요.</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <LayoutHeader
        title="이용권 목록"
        hasBackBtn
        hasFootPrint={false}
        onBackClick={handleBackClick}
      />
      <div className="flex flex-col flex-auto bg-[#F5F5F5] pb-24">
        <div className="px-4 pt-4 pb-6">
          {plans.length === 0 ? (
            <div className="flex flex-col justify-center items-center py-20">
              <p className="mb-4 text-gray-500">
                {allowedPlanSeqs !== null
                  ? "선택하신 이용권을 찾을 수 없습니다."
                  : "등록된 이용권이 없습니다."}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {plans.map((plan) => {
                const isSelected = selectedPlans.has(plan.planSeq);
                const quantity = selectedPlans.get(plan.planSeq) || 0;
                const minQty = plan.minPurchaseQty ?? 1;
                const isAllowed =
                  allowedPlanSeqs === null || allowedPlanSeqs.has(plan.planSeq);

                return (
                  <div
                    key={plan.planSeq}
                    className={`relative p-4 bg-white rounded-xl shadow-sm border transition-all overflow-hidden ${
                      !isAllowed
                        ? "border-gray-200 opacity-50 cursor-not-allowed"
                        : isSelected
                          ? "border-orange-400 cursor-pointer"
                          : "border-gray-200 cursor-pointer hover:border-gray-300"
                    }`}
                    onClick={() => isAllowed && handlePlanToggle(plan.planSeq)}
                  >
                    <div className="flex flex-col">
                      {/* 이용권 정보 */}
                      <div className="flex-1">
                        {/* 제목과 수량 조정 UI */}
                        <div className="flex gap-2 justify-between items-start mb-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-base font-semibold text-gray-800 break-words">
                              {plan.planName}
                            </h3>
                            {minQty > 1 && (
                              <p className="mt-0.5 text-xs text-orange-500">
                                최소 {minQty}개부터 구매 가능
                              </p>
                            )}
                          </div>
                          {/* 수량 조정 UI (항상 공간 확보, 선택된 경우에만 표시) */}
                          <div
                            className="flex items-center gap-2 flex-shrink-0 w-[88px] h-7 justify-end"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {isSelected ? (
                              <>
                                <button
                                  type="button"
                                  className="flex justify-center items-center w-7 h-7 text-gray-600 rounded-lg transition-colors hover:bg-gray-100"
                                  onClick={() =>
                                    handleQuantityDecrease(plan.planSeq)
                                  }
                                  disabled={quantity <= minQty}
                                  aria-label="수량 감소"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M20 12H4"
                                    />
                                  </svg>
                                </button>
                                <span className="w-8 text-sm font-semibold text-center text-gray-800">
                                  {quantity}
                                </span>
                                <button
                                  type="button"
                                  className="flex justify-center items-center w-7 h-7 text-gray-600 rounded-lg transition-colors hover:bg-gray-100"
                                  onClick={() =>
                                    handleQuantityIncrease(plan.planSeq)
                                  }
                                  aria-label="수량 증가"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 4v16m8-8H4"
                                    />
                                  </svg>
                                </button>
                              </>
                            ) : null}
                          </div>
                        </div>
                        {/* 배지 영역 */}
                        {plan.badges && plan.badges.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-2">
                            {plan.badges.map((badge, idx) => (
                              <span
                                key={idx}
                                className="px-2 h-[23px] text-[12px] font-medium text-[#111111] bg-[#EBEBEB] rounded-full whitespace-nowrap flex items-center"
                              >
                                {badge}
                              </span>
                            ))}
                          </div>
                        )}
                        {plan.content && (
                          <p className="mb-3 text-sm text-gray-600 break-words">
                            {plan.content}
                          </p>
                        )}
                        <div className="flex gap-1 items-center">
                          {plan.discountRate > 0 && (
                            <span className="text-sm text-[#959595] line-through">
                              {formatPrice(plan.price)}원
                            </span>
                          )}
                          <span className="text-base font-semibold text-[#000000]">
                            {formatPrice(plan.discountedPrice)}원
                          </span>
                          {plan.discountRate > 0 && (
                            <span className="text-xs font-medium text-[#FF0000]">
                              {plan.discountRate}%
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* 하단 고정 구매 요약 및 구매하기 버튼 */}
      {hasSelection && (
        <div className="fixed left-1/2 -translate-x-1/2 w-full max-w-[480px] bottom-0 md:bottom-6 md:rounded-b-3xl z-40 bg-white border-t border-gray-200 shadow-lg">
          <div className="px-4 pt-3 pb-[calc(0.75rem+var(--nav-bottom,0px)+env(safe-area-inset-bottom,0px))]">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">
                선택된 이용권: {totalQuantity}개
              </span>
              <div className="flex flex-col items-end">
                {totalDiscountAmount > 0 && (
                  <span className="text-xs text-gray-400 line-through mb-0.5">
                    {formatPrice(totalOriginalAmount)}원
                  </span>
                )}
                <span className="text-lg font-bold text-orange-500">
                  총 {formatPrice(totalAmount)}원
                </span>
              </div>
            </div>
            <button
              className="py-3 w-full text-base font-semibold text-white bg-orange-400 rounded-lg transition-colors hover:bg-orange-500 active:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              onClick={handlePurchase}
              disabled={isCreatingPreOrder}
            >
              {isCreatingPreOrder ? "처리 중..." : "구매하기"}
            </button>
          </div>
        </div>
      )}

      {/* 펫 선택 모달 */}
      <MessageModal
        isOpen={isPetModalOpen}
        onRequestClose={() => setIsPetModalOpen(false)}
        title="이용권을 사용할 반려동물 선택"
        showCloseButton
        shouldCloseOnOverlayClick
        className="pt-6 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl shadow-lg outline-none"
      >
        <div className="flex flex-col gap-3">
          <div className="overflow-y-auto flex flex-col gap-2 max-h-[50vh]">
            {petList && petList.length > 0 ? (
              petList.map((pet: PetDataType) => {
                const isSelected = selectedPetSeq === pet.petSeq;
                return (
                  <button
                    key={pet.petSeq}
                    type="button"
                    onClick={() => setSelectedPetSeq(pet.petSeq)}
                    className={`flex gap-3 items-center p-3 text-left rounded-lg border-2 transition-colors ${
                      isSelected
                        ? "border-orange-400 bg-orange-50"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    {pet.fileUrl ? (
                      <img
                        src={pet.fileUrl}
                        alt={pet.petName}
                        className="object-cover w-12 h-12 rounded-full"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-100 rounded-full" />
                    )}
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-base font-semibold text-gray-900 truncate">
                        {pet.petName}
                      </span>
                      {pet.petBreedName && (
                        <span className="text-xs text-gray-500 truncate">
                          {pet.petBreedName}
                        </span>
                      )}
                    </div>
                    {isSelected && (
                      <span className="px-2 py-1 text-xs font-medium text-orange-500 bg-orange-100 rounded">
                        선택됨
                      </span>
                    )}
                  </button>
                );
              })
            ) : (
              <div className="py-6 text-sm text-center text-gray-500">
                등록된 반려동물이 없습니다.
              </div>
            )}
          </div>
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={() => setIsPetModalOpen(false)}
              className="flex-1 py-3 text-base font-semibold text-gray-600 bg-white rounded-lg border border-gray-300"
            >
              취소
            </button>
            <button
              type="button"
              onClick={handleConfirmPetAndPurchase}
              disabled={selectedPetSeq == null || isCreatingPreOrder}
              className="flex-1 py-3 text-base font-semibold text-white bg-orange-400 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreatingPreOrder ? "처리 중..." : "확인"}
            </button>
          </div>
        </div>
      </MessageModal>
    </>
  );
}
